import { Link } from 'react-router-dom';
import { Plane, Building2, Users, Car, Monitor, MapPin, Truck, Snowflake } from 'lucide-react';
import { useScrollAnimation } from '@/hooks/useScrollAnimation';
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from '@/components/ui/carousel';
import { SplitSection, CheckList, CompareTable, SectionHeader } from '@/components/brand/BrandKit';
import { EUROPCAR_DUBAI_URL } from '@/i18n/navData';

const CAROUSEL_OPTS = { align: "start", loop: true };

const leasingRows = [
  ["Down Payment", "No Down Payment", "Mandatory Down Payment"],
  ["Monthly Payments", "Lower Monthly Payments", "Higher Monthly Payments"],
  ["Registration & Insurance", "Free Registration & Insurance", "Registration Costs"],
  ["Maintenance", "Free Maintenance", "Unexpected Repair Expenses"],
  ["Replacement Vehicle", "Free Replacement", "Additional Downtime Costs"],
  ["Resale", "No Resale Worries", "Depreciation"],
];

const chauffeurServices = [
  { icon: Plane, label: "Airport Transfers" },
  { icon: Building2, label: "Corporate Transfers" },
  { icon: Users, label: "Dedicated Chauffeurs" },
  { icon: Car, label: "VIP Parking" },
  { icon: Monitor, label: "Exhibition & Events" },
  { icon: MapPin, label: "Intercity Services" },
  { icon: Plane, label: "Airport Coordinators" },
];

const chauffeurFleet = [
  { name: "BMW 7 Series", type: "Luxury / VIP Sedan", img: "/fleet/bmw-7-series/hero.webp" },
  { name: "Audi A6", type: "Executive Sedan", img: "/fleet/audi-a6/front.webp" },
  { name: "GMC Yukon Denali", type: "Luxury SUV", img: "/fleet/gmc-yukon/hero.webp" },
  { name: "Mercedes-Benz V-Class", type: "Executive Van", img: "/fleet/mercedes-vclass/hero.webp" },
  { name: "Mercedes-Benz Sprinter", type: "Executive Minibus", img: "/fleet/mercedes-sprinter/hero.webp" },
];

function ServiceSection({ id, children }) {
  const [ref, visible] = useScrollAnimation();
  return (
    <section id={id} data-testid={`service-${id}`} className="scroll-mt-24">
      <div ref={ref} className={visible ? 'scroll-visible' : 'scroll-hidden'}>{children}</div>
    </section>
  );
}

function Ctas({ children }) {
  return <div className="mt-8 flex flex-col sm:flex-row gap-3">{children}</div>;
}

export function CarRentalSection() {
  return (
    <ServiceSection id="car-rental">
      <SplitSection chip="Daily · Weekly · Monthly" title="Rent the right car, anywhere in the UAE." image="https://egmg.ae/wp-content/uploads/2025/06/Resize-image-project-4.png" imageAlt="Europcar Dubai fleet"
        desc="Young, well-maintained fleet including locations at all 3 Dubai Airport terminals, Atlantis The Palm, Emirates Towers, Dubai Hills Mall, and more.">
        <CheckList className="mt-6" items={["Easy online booking", "24/7 Roadside assistance", "ISO-certified service", "International bookings in 143 countries"]} />
        <Ctas>
          <a href={EUROPCAR_DUBAI_URL} target="_blank" rel="noopener noreferrer" data-testid="rental-book-btn" className="btn-primary text-center">Book Your Rental</a>
          <Link to="/europcar" className="btn-outline text-center">Europcar Details</Link>
        </Ctas>
      </SplitSection>
    </ServiceSection>
  );
}

export function LeasingSection() {
  return (
    <ServiceSection id="vehicle-leasing">
      <SplitSection reverse bg="bg-[#FAFAFA]" chip="Corporate Solutions" title="Lease smarter. Drive further." image="/generated/leasing-fleet-corporate.jpg" imageAlt="Corporate lease fleet in Dubai"
        desc="Long-term fleet solutions for 500+ corporate clients. No down payment, free maintenance, free replacement vehicles.">
        <div className="mt-6"><CompareTable columns={["", "Leasing", "Buying"]} rows={leasingRows} highlight={1} /></div>
        <Ctas>
          <Link to="/contact" data-testid="leasing-quote-btn" className="btn-primary text-center">Request a Fleet Quote</Link>
          <Link to="/corporate-rental-lease" className="btn-outline text-center">Leasing Details</Link>
        </Ctas>
      </SplitSection>
    </ServiceSection>
  );
}

export function ChauffeurSection() {
  return (
    <ServiceSection id="chauffeur">
      <div className="bg-white py-14 sm:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
            <div className="overflow-hidden rounded-xl border border-black/[0.06]">
              <img src="/generated/emirates-taxi-hero.jpg" alt="Eurogulf chauffeur opening the door for a guest at a Dubai hotel" loading="lazy" className="h-72 sm:h-[420px] w-full object-cover" />
            </div>
            <div>
              <SectionHeader compact chip="Eurogulf Premium Chauffeur" title="Arrive in command." desc="Professional chauffeur-driven and managed transport solutions. Available for airport transfers, corporate events, staff transport, and intercity travel." />
              <p className="mt-3 font-heading font-bold text-sm text-[#EE5A01]">Managed Transport and Driven Services</p>
              <Ctas>
                <Link to="/book-chauffeur" data-testid="chauffeur-book-btn" className="btn-primary text-center">Book a Chauffeur</Link>
                <Link to="/chauffeur-service" className="btn-outline text-center">Eurogulf Premium Chauffeur Details</Link>
              </Ctas>
            </div>
          </div>

          <div className="mt-12 grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-px overflow-hidden rounded-xl border border-black/[0.06] bg-black/[0.06]">
            {chauffeurServices.map((s) => (
              <div key={s.label} className="bg-white p-4 text-center hover:bg-[#FFF4ED]/70 transition-colors">
                <s.icon className="w-5 h-5 text-[#EE5A01] mx-auto" />
                <span className="mt-2 block font-body text-xs text-[#121212]">{s.label}</span>
              </div>
            ))}
          </div>

          <p className="mt-12 font-heading font-bold text-[11px] tracking-[0.2em] uppercase text-[#666666]">Our Fleet</p>
          <Carousel opts={CAROUSEL_OPTS} className="w-full mt-4">
            <CarouselContent className="-ml-4">
              {chauffeurFleet.map((v) => (
                <CarouselItem key={v.name} className="pl-4 basis-full sm:basis-1/2 lg:basis-1/3">
                  <div className="overflow-hidden rounded-xl border border-black/[0.06] bg-white">
                    <img src={v.img} alt={v.name} className="w-full h-48 object-cover" loading="lazy" />
                    <div className="p-4">
                      <h4 className="font-heading font-bold text-sm text-[#121212]">{v.name}</h4>
                      <p className="mt-0.5 font-body text-xs text-[#666666]">{v.type}</p>
                    </div>
                  </div>
                </CarouselItem>
              ))}
            </CarouselContent>
            <CarouselPrevious className="hidden sm:flex -left-4 bg-white border-black/10 text-[#121212] hover:bg-[#EE5A01] hover:text-white hover:border-[#EE5A01]" />
            <CarouselNext className="hidden sm:flex -right-4 bg-white border-black/10 text-[#121212] hover:bg-[#EE5A01] hover:text-white hover:border-[#EE5A01]" />
          </Carousel>
        </div>
      </div>
    </ServiceSection>
  );
}

export function CoachSection() {
  return (
    <ServiceSection id="coaches">
      <SplitSection bg="bg-[#FAFAFA]" chip="Royal Limousine · Bus & Coach" title="Passenger transportation at scale." image="/managed/coach-highway-dubai.jpg" imageAlt="Eurogulf coach on a Dubai highway"
        desc="Luxury coaches and managed transport for corporate groups, events, and intercity travel.">
        <div className="mt-6 grid grid-cols-2 gap-px overflow-hidden rounded-xl border border-black/[0.06] bg-black/[0.06]">
          {["Executive Sedans", "Luxury SUVs", "Luxury Mini Vans", "Full-Size Coaches"].map((c) => (
            <div key={c} className="bg-white p-3 flex items-center gap-2">
              <span className="h-1.5 w-1.5 rounded-full bg-[#EE5A01] flex-shrink-0" />
              <span className="font-body text-sm text-[#121212]">{c}</span>
            </div>
          ))}
        </div>
        <Ctas>
          <Link to="/contact" data-testid="coach-quote-btn" className="btn-primary text-center">Get a Group Transfer Quote</Link>
          <Link to="/royal-limousine" className="btn-outline text-center">View Bus & Coach</Link>
        </Ctas>
      </SplitSection>
    </ServiceSection>
  );
}

export function TruckSection() {
  return (
    <ServiceSection id="truck-leasing">
      <SplitSection reverse chip="Truckline Transport" title="Commercial solutions. Built for business." image="https://egmg.ae/wp-content/uploads/2025/06/Adobe-Express-file-3.webp" imageAlt="Truckline commercial fleet"
        desc="Vans, trucks, and chiller units for commercial and logistics operations across the UAE.">
        <div className="mt-6 flex flex-wrap gap-2">
          {["Delivery Vans", "Box Trucks", "Chiller Units", "Flatbed Trucks"].map((t) => (
            <span key={t} className="inline-flex items-center gap-2 rounded-full border border-black/10 bg-white px-4 py-2 font-body text-xs text-[#121212]">
              {t === "Chiller Units" ? <Snowflake className="w-3 h-3 text-[#EE5A01]" /> : <Truck className="w-3 h-3 text-[#EE5A01]" />}
              {t}
            </span>
          ))}
        </div>
        <Ctas>
          <Link to="/contact" data-testid="truck-enquire-btn" className="btn-primary text-center">Enquire About Commercial Fleet</Link>
          <Link to="/truckline" className="btn-outline text-center">Truckline Details</Link>
        </Ctas>
      </SplitSection>
    </ServiceSection>
  );
}

export function UsedCarsSection() {
  return (
    <ServiceSection id="used-cars">
      <SplitSection bg="bg-[#FAFAFA]" chip="Eurogulf Used Cars" title="Quality certified. Priced right." image="/generated/hero-usedcars.jpg" imageAlt="Certified pre-owned vehicles"
        desc="Certified pre-owned vehicles from the Eurogulf Mobility Group fleet, maintained to A-Grade workshop standards.">
        <CheckList className="mt-6" items={["Multi-point inspection", "Full service history", "Competitive pricing", "Warranty available"]} />
        <Ctas>
          <Link to="/used-cars" data-testid="used-cars-btn" className="btn-primary text-center">Browse Available Stock</Link>
        </Ctas>
      </SplitSection>
    </ServiceSection>
  );
}
